import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { ROLE_LABELS } from "@/lib/roles";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Loader2, Send } from "lucide-react";

const DEFAULT_MSG = "Dear Property Owner,\n\nPlease approve the following invoices to be paid.";
const money = (n) => `$${Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export const SendInvoiceApproval = ({ open, onClose, propertyId, invoices, onSent }) => {
  const [users, setUsers] = useState([]);
  const [sel, setSel] = useState([]);
  const [extra, setExtra] = useState("");
  const [message, setMessage] = useState(DEFAULT_MSG);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!open) return;
    setMessage(DEFAULT_MSG); setExtra("");
    api.get("/invoices/approval-recipients").then((r) => {
      const list = [...r.data].sort((a, b) => b.property_ids.includes(propertyId) - a.property_ids.includes(propertyId));
      setUsers(list);
      setSel(list.filter((u) => u.role === "owner" && u.property_ids.includes(propertyId)).map((u) => u.id));
    }).catch(() => setUsers([]));
  }, [open, propertyId]);
  const toggle = (id) => setSel((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const extras = extra.split(/[,;\s]+/).map((e) => e.trim()).filter(Boolean);
  const send = async () => {
    setBusy(true);
    try {
      const r = await api.post("/invoices/send-approval", { property_id: propertyId, invoice_ids: invoices.map((i) => i.id), user_ids: sel, extra_emails: extras, message });
      toast.success(`Sent ${r.data.count} invoice(s) to ${r.data.sent_to.join(", ")}`);
      onSent?.(); onClose();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const total = invoices.reduce((s, i) => s + Number(i.amount || 0), 0);
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-xl overflow-y-auto" data-testid="send-approval-dialog">
        <DialogHeader><DialogTitle>Send {invoices.length} invoice(s) for approval · {money(total)}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label>To ({sel.length + extras.length}) — Owners, Admins, Accountants</Label>
            <div className="max-h-48 overflow-y-auto border border-border" data-testid="send-approval-recipients">
              {users.map((u) => (
                <label key={u.id} className="flex cursor-pointer items-center gap-2 border-b border-border px-3 py-1.5 text-sm last:border-0 hover:bg-secondary/60" data-testid={`send-approval-user-${u.id}`}>
                  <Checkbox checked={sel.includes(u.id)} onCheckedChange={() => toggle(u.id)} />
                  <span className="font-medium">{u.name}</span>
                  <span className="text-xs text-muted-foreground">{ROLE_LABELS[u.role] || u.role}{u.property_ids.includes(propertyId) ? " · this property" : ""}</span>
                  <span className="ml-auto truncate text-xs text-muted-foreground">{u.email}</span>
                </label>
              ))}
              {users.length === 0 && <div className="p-3 text-xs text-muted-foreground">No Owner / Admin / Accountant users.</div>}
            </div>
            <Input value={extra} onChange={(e) => setExtra(e.target.value)} placeholder="Other emails (comma separated)" data-testid="send-approval-extra" />
          </div>
          <div className="space-y-1.5">
            <Label>Message</Label>
            <Textarea rows={4} value={message} onChange={(e) => setMessage(e.target.value)} data-testid="send-approval-message" />
            <p className="text-xs text-muted-foreground">The email lists each invoice and includes a secure link (valid 14 days, no login) to view the files and Approve / Decline.</p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={send} disabled={busy || (!sel.length && !extras.length)} data-testid="send-approval-submit">
            {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />} Send
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const OwnerApprovalBadge = ({ inv }) => {
  const oa = inv.owner_approval;
  if (oa) return (
    <div className={`mt-0.5 text-[0.65rem] font-semibold ${oa.status === "approved" ? "text-emerald-600" : "text-red-600"}`} title={oa.note || ""} data-testid={`owner-approval-${inv.id}`}>
      Owner {oa.status} · {oa.by}
    </div>
  );
  if (inv.owner_approval_sent) return <div className="mt-0.5 text-[0.65rem] text-amber-600" title={`Sent to ${inv.owner_approval_sent.to.join(", ")}`} data-testid={`owner-approval-${inv.id}`}>Sent to owner</div>;
  return null;
};
